import { useState, useMemo, useCallback, useEffect } from "react";
import { BulkEditUsersModal } from "admin/components/user-management/BulkEditUsersModal";
import { BulkImportUsersModal } from "admin/components/user-management/BulkImportUsersModal";
import { BulkJobBanner } from "admin/components/user-management/BulkJobBanner";
import { CreateUserModal } from "admin/components/user-management/CreateUserModal";
import UserTable from "admin/components/user-management/UserTable";
import { useBulkJobs } from "admin/hooks/useBulkJobs";
import useCanEditAdminApp from "admin/hooks/useCanEditAdminApp";
import useUserStats from "admin/hooks/useUserStats";
import useUsers from "admin/hooks/useUsers";
import { USER_MANAGEMENT_PAGE_TITLE } from "admin/types/constants/page-titles";
import User from "admin/types/interfaces/user";
import { ChevronDown, Download, Loader2, UserCheck, Users as UsersIcon, Plus } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { StatsCard } from "~/components/ui/stats-card";
import useToast from "~/components/ui/toast";
import useAppHeader from "~/contexts/header";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import useUserPermission from "~/hooks/useUserPermission";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import { cn } from "~/lib/utils";
import { ACCESS_CONTROLLER_V1 } from "~/utils/accessController";
import { trackEvent } from "~/utils/monitoring";

const Users = ({ isNextGen = false }: { isNextGen?: boolean }) => {
  // Action buttons render in the page for the legacy admin app, or in the
  // nextgen header (via useNextGenHeader below) — never both.
  const maxContentWidth = isNextGen ? NEXTGEN_MAX_CONTENT_WIDTH : undefined;

  useDocumentTitle(USER_MANAGEMENT_PAGE_TITLE);
  useAppHeader({ title: USER_MANAGEMENT_PAGE_TITLE });

  useEffect(() => {
    trackEvent("admin_users_viewed");
  }, []);
  const { showError } = useToast();
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [bulkUploadUsersModalOpen, setBulkUploadUsersModalOpen] = useState(false);
  const [bulkEditUsersModalOpen, setBulkEditUsersModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const canEdit = useCanEditAdminApp();
  const canDownload = useUserPermission("read:admin-app");

  const { mutate } = useUsers();
  const onBulkJobComplete = useCallback(() => {
    void mutate();
  }, [mutate]);
  const { currentJob, currentIndex, total, goNext, goPrev, addEditJob, addImportJob, dismiss } =
    useBulkJobs({ onJobComplete: onBulkJobComplete });

  const handleExportCSV = useCallback(async () => {
    trackEvent("admin_users_export_csv_clicked");
    setIsExporting(true);
    try {
      const response = await fetch(`${ACCESS_CONTROLLER_V1}/users/csv`, {
        method: "GET",
        headers: { Accept: "text/csv" },
      });

      if (!response.ok) {
        throw new Error(`Failed to export CSV: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "users.csv";
      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      showError("Error exporting CSV");
      console.error("Error exporting CSV:", error);
    } finally {
      setIsExporting(false);
    }
  }, [showError]);

  const handleAddUserClick = useCallback(() => setCreateUserModalOpen(true), []);

  const handleBulkImportClick = useCallback(() => {
    trackEvent("admin_users_bulk_import_clicked");
    setBulkUploadUsersModalOpen(true);
  }, []);

  const { data: userStats, isLoading: isUserStatsLoading } = useUserStats();

  // Defined once and placed either in the nextgen header (via useNextGenHeader below)
  // or inline on the page for the legacy admin app — never both.
  const actionButtons = useMemo(
    () => (
      <div className={cn("flex items-center", isNextGen ? "gap-2" : "gap-3")}>
        {canDownload && (
          <Button
            variant="outline"
            onClick={handleExportCSV}
            size={isNextGen ? "sm" : "lg"}
            className={cn("flex items-center gap-2", isNextGen && "text-xs")}
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className={cn("animate-spin", isNextGen ? "h-4 w-4" : "h-5 w-5")} />
            ) : (
              <Download className={isNextGen ? "h-4 w-4" : "h-5 w-5"} />
            )}
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        )}
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size={isNextGen ? "sm" : "lg"}
                className={cn("flex items-center gap-2", isNextGen && "text-xs")}
              >
                <Plus className="h-4 w-4" />
                Add Users
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleAddUserClick}>New User</DropdownMenuItem>
              <DropdownMenuItem onClick={handleBulkImportClick}>Bulk Import...</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    ),
    [
      isNextGen,
      canEdit,
      canDownload,
      handleAddUserClick,
      handleBulkImportClick,
      handleExportCSV,
      isExporting,
    ]
  );

  useNextGenHeader(isNextGen ? { actions: [actionButtons] } : {});

  return (
    <div className="relative min-h-full">
      <BulkJobBanner
        job={currentJob}
        onDismiss={dismiss}
        currentIndex={currentIndex}
        total={total}
        goNext={goNext}
        goPrev={goPrev}
      />
      <div
        className="flex flex-col space-y-6 p-6"
        style={maxContentWidth ? { maxWidth: maxContentWidth, margin: "0 auto" } : undefined}
      >
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              User Management
            </h1>
            <p className="text-muted-foreground text-lg">
              Manage user accounts, permissions, and access levels
            </p>
          </div>
          {!isNextGen && actionButtons}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatsCard
            title={userStats?.enabled_users_count || 0}
            description="Total Enabled Users"
            icon={UsersIcon}
            isLoading={isUserStatsLoading}
            className="border"
          />

          <StatsCard
            title={userStats?.current_month_active_user_count || 0}
            description="Monthly Active Users (This Month)"
            icon={UserCheck}
            isLoading={isUserStatsLoading}
            className="border"
          />
        </div>

        <UserTable
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          onBulkEdit={() => {
            trackEvent("admin_users_bulk_edit_clicked", {
              user_count: selectedUsers.length,
            });
            setBulkEditUsersModalOpen(true);
          }}
        />
      </div>
      <CreateUserModal isOpen={createUserModalOpen} onClose={() => setCreateUserModalOpen(false)} />
      <BulkImportUsersModal
        isOpen={bulkUploadUsersModalOpen}
        onClose={() => setBulkUploadUsersModalOpen(false)}
        onJobStarted={addImportJob}
      />
      <BulkEditUsersModal
        isOpen={bulkEditUsersModalOpen}
        selectedUsers={selectedUsers}
        onClose={() => setBulkEditUsersModalOpen(false)}
        onJobStarted={addEditJob}
      />
    </div>
  );
};

export default Users;
